// Formatierer für alle Plattformen (AUFTRAG §7 „Zahlen über Intl, Währungen korrekt“). Locale en-GB
// (ANNAHME aus DESIGN §8), Kalendertage als YYYY-MM-DD ohne Zeitzonen-Verschiebung.
export const LOCALE = 'en-GB';

export interface Money {
  amountMinor: number;
  currency: string;
}

const cache = new Map<string, Intl.NumberFormat>();
function numberFormat(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = JSON.stringify(options);
  let format = cache.get(key);
  if (!format) {
    format = new Intl.NumberFormat(LOCALE, options);
    cache.set(key, format);
  }
  return format;
}

/** Nachkommastellen der Währung (USD 2, JPY 0 …). */
export function currencyDigits(currency: string): number {
  return numberFormat({ style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
}

export interface CurrencyOptions {
  /** Ohne Nachkommastellen, wenn glatt (Standard: true bei ganzen Beträgen). */
  trimZeros?: boolean;
  /** Kompakt: $125K, $1.2M. */
  compact?: boolean;
  /** Vorzeichen immer zeigen (+$5,000). */
  signed?: boolean;
}

/** Betrag in Haupteinheiten: formatCurrency(125000, 'USD') → "$125,000". */
export function formatCurrency(
  amount: number,
  currency: string,
  options: CurrencyOptions = {},
): string {
  const digits = currencyDigits(currency);
  const whole = Number.isInteger(amount);
  const trim = options.trimZeros ?? whole;
  return numberFormat({
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    notation: options.compact ? 'compact' : 'standard',
    minimumFractionDigits: options.compact ? 0 : trim ? 0 : digits,
    maximumFractionDigits: options.compact ? 1 : trim && whole ? 0 : digits,
    signDisplay: options.signed ? 'exceptZero' : 'auto',
  }).format(amount);
}

/** Betrag aus Minor Units (SPEC §7 `{ amountMinor, currency }`). */
export function formatMoney(money: Money, options: CurrencyOptions = {}): string {
  const amount = money.amountMinor / 10 ** currencyDigits(money.currency);
  return formatCurrency(amount, money.currency, options);
}

export function formatNumber(
  value: number,
  options: { maxDigits?: number; minDigits?: number; compact?: boolean } = {},
): string {
  return numberFormat({
    notation: options.compact ? 'compact' : 'standard',
    minimumFractionDigits: options.minDigits ?? 0,
    maximumFractionDigits: options.maxDigits ?? 2,
  }).format(value);
}

/** Bruchteil als Prozent: formatPercent(0.264) → "26.4%"; `digits` Nachkommastellen (Standard 1, ohne Nullen). */
export function formatPercent(
  fraction: number,
  options: { digits?: number; signed?: boolean; fixed?: boolean } = {},
): string {
  const digits = options.digits ?? 1;
  return numberFormat({
    style: 'percent',
    minimumFractionDigits: options.fixed ? digits : 0,
    maximumFractionDigits: digits,
    signDisplay: options.signed ? 'exceptZero' : 'auto',
  }).format(fraction);
}

/** Multiplikator mit zwei Stellen: 1.4328 → "1.43×". */
export function formatMultiplier(value: number, digits = 2): string {
  return `${formatNumber(value, { minDigits: digits, maxDigits: digits })}×`;
}

// ── Datum ───────────────────────────────────────────────────────────────────
const DAY_MS = 86_400_000;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** YYYY-MM-DD als UTC-Mitternacht (keine Verschiebung durch die lokale Zeitzone). */
export function parseDay(day: string): Date {
  const match = DATE_RE.exec(day);
  if (!match) return new Date(day);
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

/** Ganze Tage von `from` bis `to` (beide YYYY-MM-DD). */
export function daysBetween(from: string, to: string): number {
  return Math.round((parseDay(to).getTime() - parseDay(from).getTime()) / DAY_MS);
}

type DateStyle = 'short' | 'medium' | 'long' | 'dayMonth' | 'monthYear';
const DATE_OPTIONS: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  short: { day: '2-digit', month: '2-digit', year: 'numeric' },
  medium: { day: 'numeric', month: 'short', year: 'numeric' },
  long: { day: 'numeric', month: 'long', year: 'numeric' },
  dayMonth: { day: 'numeric', month: 'short' },
  monthYear: { month: 'short', year: 'numeric' },
};

/** "25 Oct 2026" (medium), "25 October 2026" (long), "25 Oct" (dayMonth), "Oct 2026" (monthYear). */
export function formatDate(value: string | Date, style: DateStyle = 'medium'): string {
  const date = typeof value === 'string' ? parseDay(value) : value;
  const options = {
    ...DATE_OPTIONS[style],
    ...(typeof value === 'string' && DATE_RE.test(value) ? { timeZone: 'UTC' } : {}),
  };
  return new Intl.DateTimeFormat(LOCALE, options).format(date);
}

/** "25 Oct 2026, 14:05" in der lokalen Zeitzone. */
export function formatDateTime(value: string | Date): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(typeof value === 'string' ? new Date(value) : value);
}

/** Fälligkeit wie in Revolut People: "Due today", "Due tomorrow", "Due in 3 days", "Overdue by 2 days". */
export function formatRelativeDue(dueOn: string, today: string): string {
  const days = daysBetween(today, dueOn);
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days > 1) return `Due in ${days} days`;
  return `Overdue by ${-days} ${days === -1 ? 'day' : 'days'}`;
}

/** Farbstufe nach R-34: überfällig rot, heute bis +2 Tage gelb, sonst neutral. */
export function dueTone(
  dueOn: string | null | undefined,
  today: string,
): 'overdue' | 'soon' | 'neutral' {
  if (!dueOn) return 'neutral';
  const days = daysBetween(today, dueOn);
  if (days < 0) return 'overdue';
  return days <= 2 ? 'soon' : 'neutral';
}

const relativeTime = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto', style: 'short' });
const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * DAY_MS],
  ['month', 30 * DAY_MS],
  ['week', 7 * DAY_MS],
  ['day', DAY_MS],
  ['hour', 3_600_000],
  ['minute', 60_000],
];

/** Relative Zeit: "2 hr ago", "yesterday", "in 3 days"; unter einer Minute "just now". */
export function formatRelativeTime(value: string | Date, now: Date = new Date()): string {
  const diff = (typeof value === 'string' ? new Date(value) : value).getTime() - now.getTime();
  if (Math.abs(diff) < 60_000) return 'just now';
  for (const [unit, ms] of UNITS) {
    if (Math.abs(diff) >= ms) return relativeTime.format(Math.round(diff / ms), unit);
  }
  return 'just now';
}

/** Initialen für Avatare: "Alice Moreau" → "AM". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}
